import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'
import CustomIconButton from '../components/IconButton'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

type Props<T> = {
    data: Array<T>
    columns: Array<ColumnDef<T>>
    withPagination?: boolean
    pageSize?: number
    headerless?: boolean
    highlightedRow?: number
    withSorting?: boolean
    classname?: string
}

function CustomTable<T>({
    data,
    columns,
    withPagination = false,
    withSorting = false,
    pageSize = 10,
    headerless = false,
    highlightedRow = -1,
}: Props<T>) {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize,
    })
    const [sorting, setSorting] = useState<SortingState>([])

    const tableConfig = useReactTable<T>({
        data,
        columns,
        defaultColumn: {
            size: 100,
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        state: {
            pagination,
            sorting,
        },
        enableSorting: withSorting,
        autoResetAll: false,
    })

    const getColumnTooltip = (column: any) => {
        if (!column.getCanSort()) return undefined
        switch (column.getNextSortingOrder()) {
            case 'asc':
                return 'Ordenar ascendente'
            case 'desc':
                return 'Ordenar descendente'
            default:
                return 'Limpiar ordenamiento'
        }
    }

    return (
        <div className="w-full">
            <table className="w-full border-collapse border-none">
                {!headerless && (
                    <thead className="border-collapse whitespace-pre border-none bg-[#F5F6FA] text-left">
                        {tableConfig.getHeaderGroups().map((headerGroup: any) => (
                            <tr
                                key={headerGroup.id}
                                className="whitespace-pre"
                            >
                                {headerGroup.headers.map((header: any) => (
                                    <th
                                        key={header.id}
                                        className="whitespace-pre px-2 py-2 text-xs text-secondary lg:text-sm"
                                    >
                                        <div
                                            className={
                                                header.column.getCanSort()
                                                    ? 'cursor-pointer select-none'
                                                    : ''
                                            }
                                            title={getColumnTooltip(header.column)}
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                            {{
                                                asc: ' 🡅',
                                                desc: ' 🡇',
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                )}
                <tbody className="border-collapse border-none">
                    {tableConfig.getRowModel().rows.map((row: any) => (
                        <tr
                            key={row.id}
                            className="border-collapse border-none"
                        >
                            {row.getVisibleCells().map((cell: any) => (
                                <td
                                    key={`${cell.column.columnDef.cell} - ${cell.id}`}
                                    className={`text:xs border-collapse border-none px-2 py-2 lg:text-sm ${highlightedRow === cell.getContext().row.index ? 'bg-light-bg' : ''}`}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {withPagination ? (
                <div className="flex justify-end">
                    <span className="flex items-center gap-1">
                        <div className="text-xs text-primary">Página </div>
                        <strong className="text-xs text-secondary">
                            {tableConfig.getState().pagination.pageIndex + 1} de{' '}
                            {tableConfig.getPageCount().toLocaleString()}
                        </strong>
                    </span>
                    <CustomIconButton
                        disabled={!tableConfig.getCanPreviousPage()}
                        icon={<KeyboardArrowLeftIcon />}
                        onClick={() => tableConfig.previousPage()}
                    />

                    <CustomIconButton
                        disabled={!tableConfig.getCanNextPage()}
                        icon={<KeyboardArrowRightIcon />}
                        onClick={() => tableConfig.nextPage()}
                    />
                </div>
            ) : null}
        </div>
    )
}

export default CustomTable
